import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfileService } from '../../services/profile.service';
import { BaseToolbarComponent, LocationOption } from '../shared/base-toolbar.component';

export type { LocationOption };

/**
 * Toolbar component for filtering and selecting campaign segments, with location selection and search support.
 * @component
 * @selector app-segments-toolbar
 * @example
 *   <app-segments-toolbar [selectedLocation]="sampleLocation" (locationChanged)="onLocationChange($event)"></app-segments-toolbar>
 * @input string selectedLocation - Optional identifier of the currently selected location. If provided, the toolbar will initialize with this location selected; otherwise the component may select a default.
 * @input Array<{value: string, selected?: boolean}> locations - Array of available locations (each item contains a value and optional selected flag) shown in the toolbar for selection.
 * @input string searchPlaceholder - Placeholder text for the segment search input. Defaults to 'Search for segments.' set in the constructor.
 * @output EventEmitter<string[]> locationChanged - Emitted whenever the set of selected locations changes; payload is an array of selected location identifiers.
 *
 * Purpose:
 *   Provides a specialized toolbar for working with campaign segments including search and location-scoped filtering. It extends a BaseToolbarComponent to reuse common toolbar behavior and integrates profile-based defaults.
 *
 * Key methods:
 *   - constructor(profileService: ProfileService): Initializes the component and sets the default searchPlaceholder to 'Search for segments.'.
 *   - onLocationsLoaded(): Lifecycle helper (override) that ensures a sensible default selected location: if no selectedLocation is present and locations are available, it selects the first location, marks it selected, adds it to the temporary selection set, and emits locationChanged. If a selectedLocation already exists it is added to the temporary selection.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): (inherited/used) standard initialization for the component lifecycle.
 *   - ngOnDestroy(): (inherited/used) teardown and cleanup when the component is destroyed.
 *
 * Notes:
 *   - Internally maintains a tempSelectedLocations set to track transient selection state.
 *   - Relies on BaseToolbarComponent for shared toolbar functionality and for triggering onLocationsLoaded when location data is available.
 */
@Component({
  selector: 'app-segments-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './segments-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './segments-toolbar.component.scss',
})
export class SegmentsToolbarComponent extends BaseToolbarComponent implements OnInit, OnDestroy {
  constructor(profileService: ProfileService) {
    super(profileService);
    this.searchPlaceholder = 'Search for segments.';
  }

  protected override onLocationsLoaded(): void {
    if (this.selectedLocation) {
      this.tempSelectedLocations.add(this.selectedLocation);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
